import React, { useMemo, useState } from 'react';
import { formatCurrency } from '../utils/formatters.util';

import StarRating from './StarRating';
import { FaArrowRightLong } from 'react-icons/fa6';
import useDesignPricing from '../hooks/useDesignPricing';
import ColorSwatches from './ColorSwatches';
import MoreColorsSwatch, { LessColorsSwatch } from '@components/tailwind/MoreColorsSwatch';
import { MIYO_DISABLED_COLORS } from '../features/pdp/settings';
import BundleSwatches from './BundleSwatches';
import { useAppSelector } from '@/store';

type Props = {
  choiceNum: number;
  displayName: string;
  bundleName: string;
  selectedItem: any;
  isFree: boolean;
  isInline: boolean;
  members: any[];
  attributesByKey: any;
  itemsById: any;
  onChange: (choiceNum: number, shopItemId: string) => void;
  onCustomize: () => void;
  miyoFee?: number;
  design: any;
};

const BundleChoice = ({
  choiceNum,
  displayName,
  bundleName,
  selectedItem,
  isFree,
  isInline,
  members,
  attributesByKey,
  itemsById,
  onChange,
  onCustomize,
  miyoFee: miyoFeeOverride,
  design,
}: Props) => {
  const site = useAppSelector((state: any) => state.pdpItem.site);
  const selectors = getSelectors(selectedItem, members, attributesByKey, itemsById);
  const miyoFee = miyoFeeOverride || selectedItem.miyo?.faceFee?.rate || 0;

  const { colorSelector, sizeSelector } = useMemo(() => {
    const colorSelector = selectors.find((s) => s.name === 'color');
    const sizeSelector = selectors.find((s) => s.name === 'size');
    return { colorSelector, sizeSelector };
  }, [selectors]);

  const onChangeLocal = (selectedOption) => {
    const changedAttributes = Object.fromEntries(selectedOption.value.split('|').map((v) => v.split('=')));
    const newAttributes = { ...selectedItem.attributes, ...changedAttributes };
    const shopItemId = getSelectedShopItemId(newAttributes, members);
    onChange(choiceNum, shopItemId);
  };

  const designPricing = useDesignPricing({
    basePrice: (isFree ? 0 : selectedItem?.rate) || 0,
    config: design?.config,
    faces: design?.designData?.faces,
    graphicType: design?.designData?.graphicType,
  });

  const previewUrls =
    [
      design?.faceProductPreviews?.map((f) => f.url) || [],
      [design?.previewUrl],
      [selectedItem.webImages.default.active.url],
    ]
      .map((urls) => urls.filter((u) => !!u))
      .find((urls) => urls.length > 0) || [];

  const isMiyoable = selectedItem.canMiyo && !MIYO_DISABLED_COLORS.includes(selectedItem.attributes.color);

  const [graphicTypes, canEngrave, canColor] = useMemo(() => {
    const config = selectedItem?.miyo?.preview?.[0]?.miyoConfig?.graphicType || {};

    const graphicTypes = Object.keys(config).filter(
      (key) => window.DD_ENV === 'dev' || config[key] === 'prod'
    );

    const canEngrave = graphicTypes.includes('engraved');
    const canColor = graphicTypes.includes('color');

    return [graphicTypes, canEngrave, canColor];
  }, [selectedItem?.miyo]);

  const handleCustomize = () => {
    if (isMiyoable) onCustomize();
  };

  const processingDays = useMemo(() => {
    const normalProcessingDays = Math.max(
      selectedItem.processing.customization['color']?.normal ?? 0,
      selectedItem.processing.customization['engraved']?.normal ?? 0
    );
    const rushProcessingDays = Math.max(
      selectedItem.processing.customization['color']?.rush ?? 0,
      selectedItem.processing.customization['engraved']?.rush ?? 0
    );
    const isRushEnabled =
      !!site?.flags?.enableCustomizationRush && normalProcessingDays !== rushProcessingDays;
    return isRushEnabled ? rushProcessingDays : normalProcessingDays;
  }, [selectedItem, site]);

  return (
    <div className='bundle-item'>
      <div className='tw:flex tw:gap-3'>
        <div className='bundle-item__img-wrapper tw:!flex-row text-center'>
          {previewUrls.map((url, i) => (
            <img
              key={i}
              className='img-fluid tw:min-w-0'
              alt={`${selectedItem.displayName} Image`}
              src={url}
            />
          ))}
        </div>
        <div className='bundle-item__details'>
          <div className='tw:flex tw:items-start'>
            <div className='tw:flex-auto tw:text-lg tw:font-bold'>{displayName}</div>
            {design && (
              <img
                src='/assets/images/rtic/myRTIC-Logo.png'
                alt='MyRTIC'
                className='tw:block tw:h-[16px] tw:flex-none'
              />
            )}
          </div>
          <p className='tw:mb-1 tw:text-sm tw:text-core-deep-navy'>
            ({formatCurrency(selectedItem.rate)} reg. price)
          </p>

          {selectedItem.rating.count > 0 && (
            <div className='bundle-item__rating'>
              <StarRating
                linkToReviews={false}
                starsColor='#1F1F1F'
                itemName={selectedItem.displayName}
                reviewRating={selectedItem.rating.rating}
                reviewCount={selectedItem.rating.count}
              />
            </div>
          )}
          {isFree && isInline && (
            <div className='bundle-item__rate'>
              <del className='text-muted'>{formatCurrency(selectedItem.rate)}</del> Free With Purchase
            </div>
          )}
          {!design && (
            <div className='tw:flex tw:flex-col tw:gap-2'>
              {colorSelector && <ColorSelector selector={colorSelector} onChange={onChangeLocal} />}
              {sizeSelector && (
                <BundleSwatches
                  label={sizeSelector.name}
                  options={sizeSelector.options.sort((a, b) => a.sortOrder - b.sortOrder)}
                  itemHandler={onChangeLocal}
                />
              )}
            </div>
          )}

          {design && (
            <>
              <div
                onClick={onCustomize}
                className={
                  'tw:border-1 tw:flex tw:cursor-pointer tw:items-center tw:gap-1 tw:rounded-sm tw:border tw:border-neutrals-light-grey tw:px-3 tw:py-3 tw:text-sm'
                }>
                <div className={'tw:grow'}>
                  {attributesByKey?.color?.[selectedItem.attributes.color]?.value}
                </div>
                <div className={'tw:flex tw:items-center tw:gap-1'}>
                  Edit Design
                  <i className={'fa fa-chevron-right text-muted'} />
                </div>
              </div>
              {!!designPricing.personalizationFees && (
                <div className='tw:my-2 tw:text-right tw:text-xs'>
                  Personalization Fees{' '}
                  <strong className={'tw:font-bold'}>
                    + {formatCurrency(designPricing.personalizationFees)}
                  </strong>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {selectedItem?.canMiyo && !design && (
        <div
          onClick={handleCustomize}
          className={
            'tw:border-1 tw:mt-2 tw:flex tw:cursor-pointer tw:items-center tw:gap-1 tw:rounded-[4px] tw:border tw:border-neutrals-light-grey tw:px-5 tw:py-3 tw:text-sm tw:transition tw:duration-500 tw:hover:bg-neutral-100'
          }>
          <div className='tw:grow'>
            <div className='tw:mb-1 tw:flex tw:items-center tw:gap-1'>
              <span>{canEngrave ? 'Engrave' : 'Personalize'} with</span>{' '}
              <img
                src='/assets/images/rtic/myRTIC-Logo.png'
                alt='MyRTIC'
                className='tw:mr-2 tw:inline-block tw:h-[14px]'
              />
            </div>
            {!!miyoFee && (
              <div className='tw:text-xs tw:text-neutrals-dark-grey'>
                starting at <span className='tw:font-bold tw:text-body'>{formatCurrency(miyoFee)}</span>
              </div>
            )}
            {!isMiyoable && (
              <p className='tw:mb-0 tw:mt-1 tw:text-2xs tw:font-normal tw:leading-tight tw:text-red-700 tw:md:text-[11px]'>
                Not available in {attributesByKey.color[selectedItem.attributes.color].value}. Please select
                another Color.
              </p>
            )}
            {isMiyoable && Boolean(processingDays) && processingDays > 0 && (
              <p className='tw:mb-0 tw:mt-1 tw:text-2xs tw:font-normal tw:leading-tight tw:text-neutrals-dark-grey tw:md:text-[11px]'>
                Please allow up to {processingDays} {processingDays === 1 ? 'day' : 'days'} to process your
                order
              </p>
            )}
          </div>
          <FaArrowRightLong size={20} />
        </div>
      )}
    </div>
  );
};

export default BundleChoice;

type BundleChoicesProps = {
  isInline: boolean;
  memberChoices: any[];
  selectedItems: any[];
  attributesByKey: any;
  itemsById: any;
  selectionTitle: string;
  onChoiceChange: (choiceNum: number, shopItemId: string) => void;
  onCustomize: (choiceNum: number) => void;
  customInfo: any;
  miyoFee?: number;
};

export const BundleChoices = ({
  isInline,
  memberChoices,
  selectedItems,
  attributesByKey,
  itemsById,
  selectionTitle,
  onChoiceChange,
  onCustomize,
  customInfo,
  miyoFee: miyoFeeOverride,
}: BundleChoicesProps) => {
  if (!memberChoices) return null;

  return (
    <div className='bundle-choices tw:w-full'>
      {memberChoices.map((choice, idx) => {
        const selectedItem = selectedItems[idx];
        if (!selectedItem) return null;
        if (isInline && idx === 0) return null;

        return (
          <div key={idx}>
            <BundleChoice
              choiceNum={idx}
              selectedItem={selectedItem}
              isInline={isInline}
              isFree={choice.isFree}
              displayName={choice.displayName}
              bundleName={selectionTitle}
              members={choice.members}
              attributesByKey={attributesByKey}
              itemsById={itemsById}
              onChange={onChoiceChange}
              onCustomize={() => onCustomize(idx)}
              miyoFee={miyoFeeOverride}
              design={customInfo?.designs?.[idx]}
            />
            {idx < memberChoices.length - 1 && <hr />}
          </div>
        );
      })}
    </div>
  );
};

const MAX_SWATCHES_SHOWN = 10;

type ColorSelectorProps = {
  selector: any;
  onChange: (color: string) => void;
};

const ColorSelector = ({ selector, onChange }: ColorSelectorProps) => {
  const [showingMore, setShowingMore] = useState(false);
  // const colors = useMemo(() => {
  //   return showingMore ?
  //    selector.options :
  //    selector.options.slice(0, MAX_SWATCHES_SHOWN - 1)
  // }, [showingMore, selector]);
  const colors = showingMore ? selector.options : selector.options.slice(0, MAX_SWATCHES_SHOWN - 1);

  return (
    <ColorSwatches
      activeColor={selector.value[0]}
      colors={colors}
      itemHandler={(color) => () => {
        onChange(color);
      }}
      swatchSize='sm'
      activeColorValueField='label'
      ignoreDisabledStyle
      swatchesAddendum={
        selector.options.length > 9 &&
        (showingMore ? (
          <LessColorsSwatch onMoreClick={() => setShowingMore(false)} size={26} />
        ) : (
          <MoreColorsSwatch
            onMoreClick={() => {
              setShowingMore(true);
            }}
            size={26}
          />
        ))
      }
    />
  );
};

function getSelectors(selectedItem, members, attributesByKey, itemsById, selectors = []) {
  if (members.length === 0) return selectors;
  if (members.length === 1 && members[0].keyAttributes.length === 0) return selectors;

  const { options, value, selectedMember } = members.reduce(
    (acc, m) => {
      const primaryItem = itemsById[m.primaryShopItemId];
      const inStock = primaryItem?.inStockNow || primaryItem?.isPresale;

      const option = m.keyAttributes.reduce(
        (acc, keyAttr) => {
          const attr = attributesByKey[keyAttr.key]?.[keyAttr.id];
          const label =
            attr && keyAttr.key !== 'material'
              ? [acc.label, attr.value].filter((v) => !!v).join(', ')
              : acc.label;
          const value = keyAttr.id
            ? [acc.value, `${keyAttr.key}=${keyAttr.id}`].filter((v) => !!v).join('|')
            : acc.value;

          return {
            isSelected: acc.isSelected && selectedItem.attributes[keyAttr.key] == attr?.id,
            label,
            value,
            sortOrder: acc.sortOrder + (attr?.sortOrder || 0),
            startColorCode: acc.startColorCode || attr?.startColorCode,
            endColorCode: acc.endColorCode || attr?.endColorCode,
            swatchImageUrl: acc.swatchImageUrl || attr?.swatchImageUrl,
            id: acc.id || attr?.id,
          };
        },
        {
          isSelected: true,
          label: '',
          value: '',
          sortOrder: 0,
          startColorCode: '',
          endColorCode: '',
          swatchImageUrl: '',
          id: 0,
        }
      );

      if (!inStock) {
        option.isDisabled = true;
        option.label = `${option.label} - Sold Out`;
      }

      return {
        options: inStock || option.isSelected ? acc.options.concat(option) : acc.options,
        value: option.isSelected ? [option] : acc.value,
        selectedMember: option.isSelected ? m : acc.selectedMember,
      };
    },
    { options: [], value: [], selectedMember: null }
  );

  const attributeKeys = members[0].keyAttributes
    .filter((a) => !!a.id && a.key !== 'material')
    .map((attr) => attr.key);
  const selector = { name: attributeKeys.join('-'), attributeKeys, value, options };

  return getSelectors(
    selectedItem,
    selectedMember.members,
    attributesByKey,
    itemsById,
    selectors.concat(selector)
  );
}

function getSelectedShopItemId(attributes, members) {
  const nearestMatch = getNearestValidMatch(attributes, members);
  return nearestMatch.member.primaryShopItemId;
}

function getNearestValidMatch(attributes, members, prevMatch = { member: null, missedAttrs: 0 }) {
  if (members.length === 0) return prevMatch;

  const exactMatch = members.find((m) =>
    m.keyAttributes.every((keyAttr) => attributes[keyAttr.key] == keyAttr.id)
  );
  if (exactMatch)
    return getNearestValidMatch(attributes, exactMatch.members, { ...prevMatch, member: exactMatch });

  const nearestMatch = members.reduce((prevBest, member) => {
    const nextMatch = getNearestValidMatch(attributes, member.members, {
      member,
      missedAttrs: prevMatch.missedAttrs + 1,
    });
    return prevBest && prevBest.missedAttrs <= nextMatch.missedAttrs ? prevBest : nextMatch;
  });

  return nearestMatch;
}
